import { GridCellProps } from '@progress/kendo-react-grid';
import { MouseEvent, useCallback, useMemo } from 'react';
import GridCellBase from 'src/components/GridCellBase';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useViewCalendarYear from 'src/hooks/useViewCalendarYear';

const ViewPeriods = (props: GridCellProps) => {
  const { dataItem } = props;
  const {
    setSelectedFYPeriod,
    selectedFYPeriod,
    latestFYPeriod,
    handleDelete,
    hasEditGLPeriodsPermission,
    setActiveFY,
    setPastFY,
    isDeletingFiscalYear,
    setIsPeriodEditMode,
  } = useViewCalendarYear();
  const isIndicatorOn = props.dataItem?.fiscalYear === selectedFYPeriod?.fiscalYear;

  const handleViewClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setSelectedFYPeriod(props.dataItem);
      setActiveFY((prev) =>
        prev.map((fy) => ({ ...fy, selected: fy.fiscalYear === props.dataItem.fiscalYear }))
      );
      setPastFY((prev) =>
        prev.map((fy) => ({ ...fy, selected: fy.fiscalYear === props.dataItem.fiscalYear }))
      );
      window.scrollTo(0, 0);
    },
    [props.dataItem, setActiveFY, setPastFY, setSelectedFYPeriod]
  );

  const handleDeleteClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      handleDelete(dataItem?.fiscalYear);
      setIsPeriodEditMode(false);
    },
    [dataItem?.fiscalYear, handleDelete, setIsPeriodEditMode]
  );

  const { messages, disabled } = useMemo(() => {
    const isCurrentYear = dataItem?.isCurrYear;
    const isLastFiscalYear = dataItem?.fiscalYear === latestFYPeriod?.fiscalYear;
    const disabled =
      dataItem?.hasTransactionData || !isLastFiscalYear || isCurrentYear || isDeletingFiscalYear;

    const messages = isCurrentYear
      ? ['Current Fiscal Year cannot be deleted']
      : dataItem?.hasTransactionData
      ? ['Record contains committed expenses and cannot be deleted']
      : !isLastFiscalYear
      ? ['Only the last Fiscal Year in a company can be deleted']
      : isDeletingFiscalYear
      ? ['Deleting Future Fiscal Year']
      : [];
    return { messages, disabled } as const;
  }, [
    dataItem?.fiscalYear,
    dataItem?.hasTransactionData,
    dataItem?.isCurrYear,
    isDeletingFiscalYear,
    latestFYPeriod?.fiscalYear,
  ]);

  return (
    <GridCellBase {...props} className="relative" disableToolTip rowSelectionEnabled>
      <div className="flex items-center flex-row justify-center absolute inset-0">
        <Button
          color="secondary"
          onClick={handleViewClick}
          size="sm"
          className={isIndicatorOn ? 'bg-info' : ''}
        >
          View Periods
        </Button>

        {hasEditGLPeriodsPermission && !dataItem?.isPast && (
          <PopUp
            popupAlign={{ horizontal: 'left', vertical: 'bottom' }}
            anchorAlign={{ horizontal: 'right', vertical: 'center' }}
            className="max-h-[180px] "
            content={
              <div className="m-1 max-w-[350px]">
                <PopUpContentWrapper type="Information" contents={messages} />
              </div>
            }
            show={disabled}
          >
            <Button color="error" onClick={handleDeleteClick} size="sm" disabled={disabled}>
              Delete
            </Button>
          </PopUp>
        )}
      </div>
    </GridCellBase>
  );
};

export default ViewPeriods;
